import { useState } from 'react';
import { shareASPWithLawyer } from '../api/lawyerSharingApi';
import { supabase } from '../../../lib/supabaseClient';

export default function LawyerShareModal({ aspId, reservationId, preCreation = false, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    lawyer_email: '',
    lawyer_name: '',
    law_firm: '',
  });

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      if (preCreation) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { error: saveError } = await supabase
          .from('asp_draft_acknowledgments')
          .upsert({
            reservation_id: reservationId,
            buyer_id: user.id,
            acknowledgments: {
              pending_lawyer_share: {
                lawyer_email: formData.lawyer_email,
                lawyer_name: formData.lawyer_name || null,
                law_firm: formData.law_firm || null
              }
            }
          }, {
            onConflict: 'reservation_id,buyer_id',
            ignoreDuplicates: false
          });

        if (saveError) throw saveError;

        if (onSuccess) onSuccess({
          success: true,
          preCreation: true,
          lawyerEmail: formData.lawyer_email
        });
        onClose();
      } else {
        const result = await shareASPWithLawyer(
          aspId,
          formData.lawyer_email,
          formData.lawyer_name || null,
          formData.law_firm || null
        );

        if (result.success) {
          if (onSuccess) onSuccess(result);
          onClose();
        } else {
          setError(result.message || 'Failed to share agreement');
        }
      }
    } catch (err) {
      console.error('Error sharing with lawyer:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Share with Your Lawyer
        </h2>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm text-blue-900 font-medium">Why share with a lawyer?</p>
              <p className="text-sm text-blue-800 mt-1">
                A property lawyer can explain the legal terms, identify potential issues,
                and ensure you understand your obligations before signing.
              </p>
            </div>
          </div>
        </div>

        {preCreation && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-yellow-800">
                Your lawyer's details will be saved. Once you create the agreement,
                we'll automatically send them a secure link to review it.
              </p>
            </div>
          </div>
        )}

        {!preCreation && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              Your lawyer will receive a secure link to review this agreement.
              The link expires in 30 days.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lawyer's Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="lawyer_email"
              value={formData.lawyer_email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="lawyer@lawfirm.co.nz"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lawyer's Name
            </label>
            <input
              type="text"
              name="lawyer_name"
              value={formData.lawyer_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Jane Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Law Firm
            </label>
            <input
              type="text"
              name="law_firm"
              value={formData.law_firm}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Smith & Partners"
            />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="text-xs text-gray-600">
                Your lawyer will receive a read-only view. They cannot sign or modify the agreement.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : preCreation ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Lawyer Details
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send to Lawyer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
